import { useMemo, useState } from "react";
import { currencyFormatter } from "../../../../../utils/formatter/formatter";
import { OPPORTUNITY_TYPE_STYLE } from "../oportunidadesSortimento.data";
import { OpportunityDetailPanel } from "./OpportunityDetailPanel";

// mesma largura de coluna usada no cabeçalho E em cada card — garante alinhamento
const COLUMN_TEMPLATE = "44px minmax(220px, 1fr) 150px 140px 150px 110px";

// ícones de ordenação (lucide: arrow-up / arrow-down / arrow-up-down)
const ArrowUpDownIcon = ({ color = "currentColor" }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21 16-4 4-4-4" /><path d="M17 20V4" /><path d="m3 8 4-4 4 4" /><path d="M7 4v16" />
  </svg>
);
const ArrowUpIcon = ({ color = "currentColor" }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m5 12 7-7 7 7" /><path d="M12 19V5" />
  </svg>
);
const ArrowDownIcon = ({ color = "currentColor" }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
  </svg>
);
const SortIcon = ({ col, sort }) => {
  if (sort.col !== col) return <ArrowUpDownIcon color="#999" />;
  return sort.dir === "asc" ? <ArrowUpIcon color="#E8773A" /> : <ArrowDownIcon color="#E8773A" />;
};

// chevron do "Detalhes/Ocultar" — mesmo svg, só gira 180° quando aberto
const ChevronIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// pill verde do ROI ("↗ R$ 79.362") — agora com borda, igual às outras pills
const RoiBadge = ({ value }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px",
    borderRadius: "6px", backgroundColor: "rgba(22,163,74,0.08)", color: "#16a34a",
    border: "1px solid rgba(22,163,74,0.3)", fontWeight: "700", fontSize: "12px", width: "fit-content",
  }}>
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 7h6v6" /><path d="m22 7-8.5 8.5-5-5L2 17" />
    </svg>
    {currencyFormatter.format(value)}
  </span>
);

const GroupPill = ({ label }) => (
  <span style={{
    display: "inline-block", padding: "4px 12px", borderRadius: "6px",
    border: "1px solid #efefef", fontSize: "12px", color: "#555", fontWeight: "500",
    backgroundColor: "#fafafa", width: "fit-content",
  }}>
    {label}
  </span>
);

const TypePill = ({ type }) => {
  const style = OPPORTUNITY_TYPE_STYLE[type] || OPPORTUNITY_TYPE_STYLE["A verificar"];
  return (
    <span style={{
      padding: "3px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "700",
      color: style.color, backgroundColor: style.bg, border: `1px solid ${style.border}`, width: "fit-content",
    }}>
      {type}
    </span>
  );
};

const HEADER_COLUMNS = [
  { key: "rank", label: "#", sortable: true },
  { key: "name", label: "PRODUTO", sortable: false },
  { key: "roi", label: "RETORNO (ROI)", sortable: true },
  { key: "group", label: "GRUPO", sortable: true },
  { key: "type", label: "TIPO", sortable: true },
  { key: "action", label: "AÇÃO", sortable: false },
];

export const OpportunityTable = ({ opportunities }) => {
  const [sort, setSort] = useState({ col: "rank", dir: "asc" });
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSort = (col) => {
    setSort((prev) => (prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }));
  };

  const sortedRows = useMemo(() => {
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...opportunities].sort((a, b) =>
      typeof a[sort.col] === "string"
        ? a[sort.col].localeCompare(b[sort.col]) * factor
        : (a[sort.col] - b[sort.col]) * factor
    );
  }, [opportunities, sort]);

  return (
    <div>
      <style>{`
        .opp-th-cell { color: #888; transition: color 0.15s; }
        .opp-th-cell.sortable { cursor: pointer; }
        .opp-th-cell.sortable:hover { color: #555; }
        .opp-card { transition: box-shadow 0.15s, border-color 0.15s; }
        .opp-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.08); border-color: #e2e2e2; }
        .opp-toggle { color: #888; transition: color 0.15s; }
        .opp-card:hover .opp-toggle, .opp-toggle.is-open { color: #E8773A; }
      `}</style>

      {/* cabeçalho — mesmo gap:8 dos cards, senão a coluna flexível (PRODUTO)
          fica com larguras diferentes e desalinha tudo depois dela */}
      <div style={{
        display: "grid", gridTemplateColumns: COLUMN_TEMPLATE, gap: 8, alignItems: "center",
        padding: "10px 16px", backgroundColor: "#f8f9fb", border: "1px solid #eee", borderRadius: "8px",
        marginBottom: 8, position: "sticky", top: 0, zIndex: 10,
      }}>
        {HEADER_COLUMNS.map((col) => (
          <div
            key={col.key}
            className={`opp-th-cell${col.sortable ? " sortable" : ""}`}
            onClick={col.sortable ? () => toggleSort(col.key) : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 4, padding: "4px 6px", borderRadius: 6,
              fontSize: "10.5px", fontWeight: "700", letterSpacing: "0.04em",
              justifyContent: (col.key === "rank" || col.key === "name") ? "flex-start" : "center",
            }}
          >
            {col.label}
            {col.sortable && <SortIcon col={col.key} sort={sort} />}
          </div>
        ))}
      </div>

      {/* cards de produto — separados, com sombra no hover */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sortedRows.map((item) => {
          const isExpanded = expandedIds.has(item.id);
          return (
            <div key={item.id} className="opp-card" style={{ border: `1px solid ${isExpanded ? "#999" : "#eee"}`, borderRadius: "8px", background: "#fff", overflow: "hidden" }}>
              <div
                onClick={() => toggleExpanded(item.id)}
                style={{
                  display: "grid", gridTemplateColumns: COLUMN_TEMPLATE, alignItems: "center", justifyItems: "center",
                  padding: "14px 16px", gap: 8, cursor: "pointer",
                  backgroundColor: isExpanded ? "#e8ecf1" : "transparent",
                }}
              >
                <span style={{ fontSize: "12.5px", color: "#bbb", justifySelf: "start" }}>{item.rank}</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1a1a1a", justifySelf: "start" }}>{item.name}</span>
                <RoiBadge value={item.roi} />
                <GroupPill label={item.group} />
                <TypePill type={item.type} />
                {/* visual apenas — o clique é tratado pelo card inteiro, acima */}
                <span
                  className={`opp-toggle${isExpanded ? " is-open" : ""}`}
                  style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "12.5px", fontWeight: "600" }}
                >
                  {isExpanded ? "Ocultar" : "Detalhes"}
                  <ChevronIcon open={isExpanded} />
                </span>
              </div>

              {isExpanded && <OpportunityDetailPanel opportunity={item} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
